import React, { useRef, useState } from 'react';
import { useMount } from 'react-use';
import { BoxShareLinkInterface } from 'src/common/interfaces/integrations/box.interface';
import OrganizationLink from 'src/common/OrganizationLink';
import { BoxStatus } from 'src/dashboard/library/content/add-section';
import { DashboardLibraryContentPickBoxFileModal } from 'src/dashboard/library/content/pick-box-file-modal';
import { BoxIntegrationIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { functionNoop } from 'src/utils/function/noop';
import { useParagon } from 'src/utils/use-paragon.hook';
import { NeueButtonBase } from '../components/neue-button/base';

interface Props {
  onNewUrl: (url: string, title: string) => void;
}

export const IntegrationBoxPanel = ({ onNewUrl }: Props) => {
  const [boxStatus, setBoxStatus] = useState<typeof BoxStatus[keyof typeof BoxStatus]>(BoxStatus.Idle);
  const boxFilesModalOpenRef = useRef(functionNoop);

  const { currentUser } = useCurrentUser();
  const { currentOrganization } = useCurrentOrganization();

  const paragon = useParagon(currentUser!, currentOrganization);

  useMount(() => {
    const initParagonBoxIntegration = async () => {
      try {
        await paragon.initialize();
        await paragon.authenticate();
        const isBoxIntegrationActive = paragon.isIntegrationActive('box');
        return isBoxIntegrationActive;
      } catch (err) {
        return false;
      }
    };

    initParagonBoxIntegration().then((isActive) => {
      if (isActive) {
        setBoxStatus(BoxStatus.Connected);
      } else {
        setBoxStatus(BoxStatus.Disconnected);
      }
    });
  });

  const isBoxDisconnected = boxStatus === BoxStatus.Disconnected;

  if (isBoxDisconnected) {
    return (
      <div className='flex flex-col space-y-4 text-neue-journey-fg'>
        <div className='text-neue-journey-small'>Connect your box account and add content directly from box</div>
        <OrganizationLink
          className='flex items-center btn-bedrock-secondary px-4 py-1 space-x-1 w-40'
          to='/integrations/box'
        >
          <NeueButtonBase
            className={'py-[9px] px-4 text-neue-journey-fg w-full'}
            baseColor={'rgb(var(--neue-journey-fg-10))'}
            gradientMaxOpacity={0.1}
          >
            <BoxIntegrationIcon className='w-6 h-6 m-auto' />
            <span>Connect box</span>
          </NeueButtonBase>
        </OrganizationLink>
      </div>
    );
  }

  const onBoxFilePicked = (boxLink: BoxShareLinkInterface) => {
    return onNewUrl(boxLink.url, boxLink.name);
  };

  return (
    <div className='flex flex-col space-y-4 text-neue-journey-fg w-full'>
      <div className='text-neue-journey-small'>Pick yourt files from Box to add to your content to the library</div>
      <NeueButtonBase
        className={'py-[9px] px-4 text-neue-journey-fg w-full'}
        baseColor={'rgb(var(--neue-journey-fg-10))'}
        gradientMaxOpacity={0.1}
        onClick={() => {
          boxFilesModalOpenRef.current();
        }}
      >
        <div className='flex flex-1 items-center space-x-2'>
          <BoxIntegrationIcon className='w-5 h-5' />
          <div>Pick from Box</div>
        </div>
      </NeueButtonBase>
      <DashboardLibraryContentPickBoxFileModal openRef={boxFilesModalOpenRef} onBoxFilePick={onBoxFilePicked} />
    </div>
  );
};
